import { useRef, useState, useEffect, useCallback } from 'react';

import { CONTROLS_HIDE_TIMEOUT } from '../../constants';

type UseElementsVisibilityOptions = {
  enableTitle: boolean;
  enableControls: boolean;
  isPlaying: boolean;
};

export const useElementsVisibility = ({
  enableTitle,
  enableControls,
  isPlaying,
}: UseElementsVisibilityOptions) => {
  const [showTitle, setShowTitle] = useState(enableTitle && !isPlaying);
  const [showControls, setShowControls] = useState(
    enableControls && !isPlaying,
  );
  const hideTitleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTitleTimeout = useCallback(() => {
    if (hideTitleTimeoutRef.current) {
      clearTimeout(hideTitleTimeoutRef.current);
      hideTitleTimeoutRef.current = null;
    }
  }, []);

  const clearHideControlsTimeout = useCallback(() => {
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
      hideControlsTimeoutRef.current = null;
    }
  }, []);

  const startHideTitleTimeout = useCallback(() => {
    clearHideTitleTimeout();
    hideTitleTimeoutRef.current = setTimeout(() => {
      setShowTitle(false);
    }, CONTROLS_HIDE_TIMEOUT);
  }, [clearHideTitleTimeout]);

  const startHideControlsTimeout = useCallback(() => {
    clearHideControlsTimeout();
    hideControlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, CONTROLS_HIDE_TIMEOUT);
  }, [clearHideControlsTimeout]);

  const handleMouseEnter = useCallback(() => {
    if (enableControls) {
      setShowControls(true);
      clearHideControlsTimeout();
    }

    if (enableTitle) {
      setShowTitle(true);
      clearHideTitleTimeout();
    }
  }, [
    enableControls,
    enableTitle,
    clearHideControlsTimeout,
    clearHideTitleTimeout,
  ]);

  const handleMouseMove = useCallback(() => {
    if (enableControls) {
      setShowControls(true);
      startHideControlsTimeout();
    }

    if (enableTitle) {
      setShowTitle(true);
      startHideTitleTimeout();
    }
  }, [
    enableControls,
    enableTitle,
    startHideControlsTimeout,
    startHideTitleTimeout,
  ]);

  const handleMouseLeave = useCallback(() => {
    if (enableControls) {
      setShowControls(false);
      clearHideControlsTimeout();
    }

    if (enableTitle) {
      setShowTitle(false);
      clearHideTitleTimeout();
    }
  }, [
    enableControls,
    enableTitle,
    clearHideControlsTimeout,
    clearHideTitleTimeout,
  ]);

  const handleFocus = useCallback(() => {
    if (enableControls) {
      setShowControls(true);
      clearHideControlsTimeout();
    }

    if (enableTitle) {
      setShowTitle(true);
      clearHideTitleTimeout();
    }
  }, [
    enableControls,
    enableTitle,
    clearHideControlsTimeout,
    clearHideTitleTimeout,
  ]);

  useEffect(() => {
    return () => {
      clearHideControlsTimeout();
      clearHideTitleTimeout();
    };
  }, [clearHideControlsTimeout, clearHideTitleTimeout]);

  return {
    showControls: enableControls && (!isPlaying || showControls),
    showTitle: enableTitle && (!isPlaying || showTitle),
    handleMouseEnter,
    handleMouseMove,
    handleMouseLeave,
    handleFocus,
  };
};
